import { useState, useEffect } from 'react'
import { useGetAdminExternalAgentsQuery, useReviewExternalAgentMutation, useAdminSandboxDispatchMutation, useAdminCheckAgentHealthMutation, useAdminPollInvocationQuery, useAdminGetMatchingDiscussionsQuery, useAdminDeleteAgentMutation } from '../../store/api/developerApi'
import { useGetAdminCategoriesQuery } from '../../store/api/agentsApi'
import { Tabs, Button, EmptyState, Modal, AgentStatusBadge, CodeBlock, CopyButton } from '../../components/ui'
import { timeAgoShort } from '../../lib/avatar'
import {
  COLOR_SUCCESS, COLOR_SUCCESS_BG, COLOR_SUCCESS_BORDER,
  COLOR_ERROR,   COLOR_ERROR_BG,   COLOR_ERROR_BORDER,
  COLOR_WARNING, COLOR_CYAN,       COLOR_CYAN_ICON_BG,
  CATEGORY_BADGE_PREVIEW_MAX,
  SANDBOX_RESPONSE_TIMEOUT_S, SANDBOX_INVOCATION_POLL_MS, SANDBOX_TIMEOUT_WARNING_THRESHOLD_S,
} from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

// Per-status color map — maps agent.status → semantic color tokens
const STATUS_COLOR: Record<string, string> = {
  pending_review: COLOR_WARNING,
  active:         COLOR_SUCCESS,
  rejected:       COLOR_ERROR,
}

const INACTIVE_BORDER = '#6b7280'

function DetailRow({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.78rem' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: valueColor ?? 'var(--text-primary)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

// Turns raw error strings / HTTP codes into plain-English explanations a
// non-technical reviewer can act on (or at least paste into a rejection note).
function explainFailure(error?: string | null, statusCode?: number): string {
  if (error?.startsWith('Timed out'))                     return "Their server took too long to respond (over the time limit)."
  if (error === 'Connection failed')                      return "We couldn't reach their server. The URL may be wrong, or their server may be down."
  if (error === 'Endpoint hostname could not be resolved') return "We couldn't find their server — double-check the web address is correct."
  if (statusCode === 401 || statusCode === 403)            return "Their server rejected the request — likely an authentication problem on their end."
  if (statusCode === 404)                                  return "Their server said the page wasn't found — the web address may be wrong."
  if (statusCode && statusCode >= 500)                     return "Their server ran into an error while handling the test."
  if (error)                                                return error
  return `Their server gave an unexpected response (HTTP ${statusCode}).`
}

// Renders a duration the way a non-technical reader would say it out loud.
function fmtDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

// Summarizes the knowledge-base document counts — "ok" mirrors the same
// at-least-one-ready-file rule finalizeAgent() enforces server-side, which
// only applies to hosted agents (external agents answer from their own
// server, so a knowledge base is optional for them — pass required: false).
function describeKnowledgeBase(agent: {
  knowledgeFileCount?: number; knowledgeFileReadyCount?: number
  knowledgeFileProcessingCount?: number; knowledgeFileFailedCount?: number
}, required: boolean): { text: string; ok: boolean } {
  const total      = agent.knowledgeFileCount ?? 0
  const ready      = agent.knowledgeFileReadyCount ?? 0
  const processing = agent.knowledgeFileProcessingCount ?? 0
  const failed     = agent.knowledgeFileFailedCount ?? 0
  if (total === 0) return { text: required ? 'No documents uploaded' : 'No documents uploaded (optional for external agents)', ok: !required }
  const parts = [`${ready} ready`]
  if (processing > 0) parts.push(`${processing} still processing`)
  if (failed > 0)      parts.push(`${failed} failed`)
  return { text: `${total} uploaded — ${parts.join(', ')}`, ok: !required || ready > 0 }
}

// Waits for the developer's server to actually call back with a real answer
// (POST /api/v1/agent-response) after the initial webhook ack succeeds —
// an ack alone only proves they received the question, not that they can
// answer it. Mirrors the same round-trip check already on the developer's
// own "Sandbox Test" panel (SandboxResponseWaiter in DeveloperPage.tsx).
function AdminSandboxWaiter({ agentId, invocationId }: { agentId: string; invocationId: string }) {
  const [elapsed, setElapsed]   = useState(0)
  const [timedOut, setTimedOut] = useState(false)
  const [responded, setResponded] = useState(false)

  const { data } = useAdminPollInvocationQuery(
    { agentId, invocationId },
    { pollingInterval: timedOut || responded ? 0 : SANDBOX_INVOCATION_POLL_MS }
  )
  const inv = data?.invocation

  useEffect(() => {
    if (inv?.status === 'responded') setResponded(true)
  }, [inv?.status])

  useEffect(() => {
    if (timedOut || responded) return
    const interval = setInterval(() => {
      setElapsed(e => {
        const next = e + 1
        if (next >= SANDBOX_RESPONSE_TIMEOUT_S) { setTimedOut(true); clearInterval(interval) }
        return next
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [timedOut, responded])

  if (inv?.status === 'responded' && inv.validation) {
    const { checks, passed } = inv.validation
    return (
      <div style={{ marginTop: '0.5rem', borderRadius: 6, border: `1px solid ${passed ? COLOR_SUCCESS_BORDER : COLOR_ERROR_BORDER}`, background: passed ? COLOR_SUCCESS_BG : COLOR_ERROR_BG, padding: '0.5rem 0.65rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontWeight: 700, fontSize: '0.78rem', color: passed ? COLOR_SUCCESS : COLOR_ERROR }}>
            {passed ? '✅ They sent back a valid answer' : '⚠️ They replied, but something looks off'}
          </span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>in {fmtDuration(inv.responseMs ?? 0)}</span>
        </div>
        {!passed && (
          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem', fontSize: '0.7rem', color: COLOR_ERROR }}>
            {!checks.contentNonEmpty && <li>The reply was empty</li>}
            {!checks.withinTimeWindow && <li>It took too long to reply</li>}
          </ul>
        )}
        {inv.responseBody && (
          <div style={{ position: 'relative', marginTop: '0.4rem' }}>
            <CodeBlock maxHeight={100} fontSize="0.68rem">{inv.responseBody}</CodeBlock>
            <div style={{ position: 'absolute', top: '0.3rem', right: '0.3rem' }}>
              <CopyButton text={inv.responseBody} small />
            </div>
          </div>
        )}
      </div>
    )
  }

  if (timedOut) {
    return (
      <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: COLOR_ERROR }}>
        ⏱ {SANDBOX_RESPONSE_TIMEOUT_S}s passed — they never sent back an answer via <code>POST /api/v1/agent-response</code>.
      </div>
    )
  }

  const remaining = SANDBOX_RESPONSE_TIMEOUT_S - elapsed
  return (
    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <style>{`@keyframes admin-sandbox-spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ width: 12, height: 12, border: '2px solid var(--border)', borderTopColor: COLOR_WARNING, borderRadius: '50%', animation: 'admin-sandbox-spin 0.8s linear infinite', flexShrink: 0 }} />
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
        Waiting for their real answer… <span style={{ color: remaining <= SANDBOX_TIMEOUT_WARNING_THRESHOLD_S ? COLOR_ERROR : 'var(--text-muted)', fontWeight: 700 }}>{remaining}s</span>
      </span>
    </div>
  )
}

function AgentReviewModal({ agent, categories, onReviewed, onClose }: {
  agent: any
  categories: { id: string; name: string; icon: string | null; color: string | null }[]
  onReviewed: () => void
  onClose: () => void
}) {
  const isHosted = agent.agentType === 'hosted'
  const [reviewNote, setReviewNote] = useState(agent.reviewNote ?? '')
  // For hosted agents there's no developer server to test, so those checks
  // don't apply — treat them as satisfied rather than showing broken tests.
  const [checklist, setChecklist] = useState({
    serverOnline:    isHosted ? true : agent.healthStatus === 'reachable',
    acceptedTest:    isHosted,
    // Knowledge base is only mandatory for hosted agents — external agents
    // answer from their own server, so this is pre-satisfied for them.
    hasKnowledgeBase: !isHosted || (agent.knowledgeFileReadyCount ?? 0) > 0,
    meaningfulDescription: !!(agent.description?.trim()),
  })
  const [sandboxResult, setSandboxResult]   = useState<any>(null)
  const [sandboxRunning, setSandboxRunning] = useState(false)
  const [reviewAgent]                       = useReviewExternalAgentMutation()
  const [adminSandbox]                      = useAdminSandboxDispatchMutation()
  const [adminHealth, { isLoading: healthChecking }] = useAdminCheckAgentHealthMutation()
  const [healthResult, setHealthResult]     = useState<{ reachable: boolean; latencyMs: number; error?: string | null } | null>(
    agent.healthStatus ? { reachable: agent.healthStatus === 'reachable', latencyMs: agent.healthLatencyMs ?? 0 } : null
  )
  const { data: matchData } = useAdminGetMatchingDiscussionsQuery(agent.id)
  const [reviewingId, setReviewingId]       = useState<string | null>(null)
  const agentCats = categories.filter(c => agent.categoryIds?.includes(c.id))
  const allChecked = Object.values(checklist).every(Boolean)

  const handleHealth = async () => {
    const res = await adminHealth(agent.id).unwrap().catch(() => null)
    if (res) { setHealthResult(res); if (res.reachable) setChecklist(p => ({ ...p, serverOnline: true })) }
  }

  const handleSandbox = async () => {
    setSandboxRunning(true); setSandboxResult(null)
    const res = await adminSandbox(agent.id).unwrap().catch((e: any) => ({ error: e?.data?.message ?? 'Failed', success: false, statusCode: 0, responseMs: 0 }))
    setSandboxResult(res)
    if (res.success) setChecklist(p => ({ ...p, acceptedTest: true }))
    setSandboxRunning(false)
  }

  const handleReview = async (status: 'active' | 'rejected') => {
    setReviewingId(status)
    try { await reviewAgent({ id: agent.id, status, reviewNote: reviewNote.trim() || undefined }).unwrap(); onReviewed(); onClose() } catch {}
    setReviewingId(null)
  }

  const statusLabel: Record<string, string> = { pending_review: 'Pending Review', active: 'Approved', rejected: 'Rejected' }

  return (
    <Modal onClose={onClose} maxWidth="600px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: (agent.color ?? '#7c3aed') + '22', border: `1px solid ${agent.color ?? '#7c3aed'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>{agent.icon ?? '🤖'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{agent.name}</span>
            <AgentStatusBadge status={agent.status} />
            {agent.resubmittedAt && <span style={{ padding: '0.15rem 0.5rem', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, background: COLOR_CYAN_ICON_BG, color: COLOR_CYAN }}>🔄 Resubmitted</span>}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
            by <strong>{agent.developer?.orgName ?? 'Unknown'}</strong>
            {agent.developer?.user && <span> · {agent.developer.user.displayName}</span>}
            <span> · {timeAgoShort(agent.createdAt)}</span>
          </div>
        </div>
      </div>

      {agent.description && <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>{agent.description}</p>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.72rem' }}>{isHosted ? '🌐' : '🔗'}</span>
        {isHosted
          ? <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
              Runs on Ekkam's hosted AI{agent.hostedProvider ? ` — ${agent.hostedProvider}${agent.hostedModel ? ` / ${agent.hostedModel}` : ''}` : ''}
            </span>
          : <code style={{ fontSize: '0.72rem', color: 'var(--accent-primary)', wordBreak: 'break-all' }}>{agent.externalEndpoint}</code>}
      </div>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {isHosted
          ? "This agent runs on Ekkam's own AI infrastructure, not a developer's server — there's nothing to connection-test. Just confirm the description below is accurate."
          : "Before approving, confirm the developer's server is online and responds correctly to a test question."}
      </p>
      {agentCats.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {agentCats.map(cat => (
            <span key={cat.id} style={{ fontSize: '0.68rem', padding: '0.1rem 0.4rem', borderRadius: 20, background: `${cat.color ?? 'var(--accent-primary)'}18`, color: cat.color ?? 'var(--accent-primary)', border: `1px solid ${cat.color ?? 'var(--accent-primary)'}33` }}>{cat.icon} {cat.name}</span>
          ))}
        </div>
      )}

      <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid var(--border)', marginBottom: '0.75rem' }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>📋 What the developer submitted</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <DetailRow label="Agent type" value={isHosted ? 'Hosted — runs on Ekkam’s AI' : 'External — runs on their own server'} />
          {isHosted && <DetailRow label="AI provider" value={`${agent.hostedProvider ?? '—'}${agent.hostedModel ? ` / ${agent.hostedModel}` : ''}`} />}
          {isHosted && <DetailRow label="Uses their own API key" value={agent.hasApiKey ? 'Yes' : "No — uses Ekkam's shared key"} />}
          <DetailRow label="Knowledge base" value={describeKnowledgeBase(agent, isHosted).text} valueColor={describeKnowledgeBase(agent, isHosted).ok ? undefined : COLOR_ERROR} />
          {agent.suggestedCategoryName && (
            <DetailRow label="Suggested category" value={`"${agent.suggestedCategoryName}"`} valueColor="var(--warning)" />
          )}
        </div>
        {agent.suggestedCategoryName && (
          <div style={{ marginTop: '0.5rem', padding: '0.45rem 0.65rem', background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 6, fontSize: '0.75rem', color: 'var(--warning)', lineHeight: 1.5 }}>
            💡 Developer suggested a new category: <strong>"{agent.suggestedCategoryName}"</strong> — create it in Categories admin if needed, then assign it to this agent via Edit.
          </div>
        )}
        {isHosted && agent.systemPrompt && (
          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>System prompt (defines how the agent behaves)</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem 0.6rem', maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {agent.systemPrompt}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {!isHosted && (
            <div style={{ flex: 1, minWidth: '180px', background: 'var(--bg-elevated)', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>🟢 Is their server online?</div>
              {healthResult ? (
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: healthResult.reachable ? COLOR_SUCCESS : COLOR_ERROR }}>
                  {healthResult.reachable ? `✔ Online — replied in ${fmtDuration(healthResult.latencyMs)}` : '✘ Not responding'}
                  {healthResult.error && <div style={{ fontSize: '0.68rem', fontWeight: 400, marginTop: '0.25rem', color: COLOR_ERROR }}>{explainFailure(healthResult.error)}</div>}
                </div>
              ) : <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Not checked yet</div>}
              <button onClick={() => void handleHealth()} disabled={healthChecking} style={{ marginTop: '0.35rem', padding: '0.2rem 0.55rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.7rem', color: 'var(--accent-primary)', cursor: healthChecking ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {healthChecking ? 'Checking…' : '↺ Check now'}
              </button>
            </div>
          )}
          <div style={{ flex: 1, minWidth: '180px', background: 'var(--bg-elevated)', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>📊 How many discussions would this reach?</div>
            {matchData ? (
              <>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: matchData.matchCount > 0 ? 'var(--accent-primary)' : 'var(--text-muted)' }}>{matchData.matchCount} discussion{matchData.matchCount !== 1 ? 's' : ''}</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.3rem' }}>{matchData.categoryIds.length === 0 ? 'in the last 7 days (all topics)' : 'matching its topics in the last 7 days'}</span>
                {matchData.sample.map((d: any) => <div key={d.id} style={{ fontSize: '0.67rem', color: 'var(--text-secondary)', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>» {d.title}</div>)}
              </>
            ) : <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Loading…</div>}
          </div>
        </div>

        {isHosted ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: COLOR_SUCCESS_BG, borderRadius: 8, padding: '0.6rem 0.75rem', border: `1px solid ${COLOR_SUCCESS_BORDER}` }}>
            <span style={{ fontSize: '1.1rem' }}>✅</span>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              No connection test needed — this agent runs on Ekkam's own AI infrastructure, not a developer's server.
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🧪 Test this agent</div>
              <button onClick={() => void handleSandbox()} disabled={sandboxRunning} style={{ padding: '0.25rem 0.7rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, cursor: sandboxRunning ? 'not-allowed' : 'pointer', opacity: sandboxRunning ? 0.7 : 1 }}>
                {sandboxRunning ? '⏳ Sending…' : '▶ Send test question'}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.3rem', marginBottom: sandboxResult ? '0.5rem' : 0 }}>
              Sends a sample question to their server, the same way a real user's question would be delivered.
            </div>
            {sandboxResult && (
              <div style={{ borderRadius: 6, border: `1px solid ${sandboxResult.success ? COLOR_SUCCESS_BORDER : COLOR_ERROR_BORDER}`, background: sandboxResult.success ? COLOR_SUCCESS_BG : COLOR_ERROR_BG, padding: '0.45rem 0.6rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: sandboxResult.success ? COLOR_SUCCESS : COLOR_ERROR }}>
                  {sandboxResult.success ? `✅ Their server accepted the request — in ${fmtDuration(sandboxResult.responseMs)}` : `❌ Test failed — in ${fmtDuration(sandboxResult.responseMs)}`}
                </div>
                {!sandboxResult.success && <div style={{ fontSize: '0.7rem', color: COLOR_ERROR, marginTop: '0.25rem' }}>{explainFailure(sandboxResult.error, sandboxResult.statusCode)}</div>}
                {sandboxResult.success && <div style={{ fontSize: '0.68rem', color: COLOR_WARNING, marginTop: '0.25rem' }}>💡 This only confirms they received the question — it doesn't yet confirm they sent back a usable answer.</div>}
                {sandboxResult.success && sandboxResult.invocationId && (
                  <AdminSandboxWaiter key={sandboxResult.invocationId} agentId={agent.id} invocationId={sandboxResult.invocationId} />
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: '0.6rem 0.75rem', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.45rem' }}>✓ Before you approve</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {(isHosted
              ? ([
                  { key: 'serverOnline', label: "Runs on Ekkam's hosted AI — no server to test", auto: true },
                  { key: 'hasKnowledgeBase', label: 'Has at least one processed knowledge document', auto: true },
                  { key: 'meaningfulDescription', label: 'Description clearly explains what this agent does', auto: false },
                ] as const)
              : ([
                  { key: 'serverOnline', label: 'Their server is online', auto: true },
                  { key: 'acceptedTest', label: 'Accepted the test question', auto: true },
                  { key: 'meaningfulDescription', label: 'Description clearly explains what this agent does', auto: false },
                ] as const)
            ).map(item => (
              <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: item.auto ? 'default' : 'pointer', fontSize: '0.8rem', color: checklist[item.key] ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${checklist[item.key] ? COLOR_SUCCESS : 'var(--border)'}`, background: checklist[item.key] ? COLOR_SUCCESS : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  onClick={() => { if (!item.auto) setChecklist(p => ({ ...p, [item.key]: !p[item.key] })) }}>
                  {checklist[item.key] && <svg width="9" height="9" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
                </div>
                <span>{item.label}</span>
                {item.auto && <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>auto</span>}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>
            Review Note <span style={{ fontWeight: 400, textTransform: 'none', opacity: 0.8 }}>(visible to developer · required to reject)</span>
          </div>
          <textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} placeholder="e.g. Endpoint returned 401 — fix HMAC verification and resubmit." rows={2} style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: '0.8rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '0.25rem' }}>
          <button onClick={onClose} style={{ padding: '0.4rem 0.9rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)', fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => void handleReview('rejected')} disabled={!!reviewingId || !reviewNote.trim()} style={{ padding: '0.4rem 1rem', background: COLOR_ERROR_BG, color: COLOR_ERROR, border: `1px solid ${COLOR_ERROR_BORDER}`, borderRadius: 8, cursor: (reviewingId || !reviewNote.trim()) ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 600, opacity: (reviewingId || !reviewNote.trim()) ? 0.5 : 1 }}>
            {reviewingId === 'rejected' ? '…' : '✗ Reject'}
          </button>
          <button onClick={() => void handleReview('active')} disabled={!!reviewingId || !allChecked} style={{ padding: '0.4rem 1rem', background: allChecked ? COLOR_SUCCESS_BG : 'var(--bg-elevated)', color: allChecked ? COLOR_SUCCESS : 'var(--text-muted)', border: `1px solid ${allChecked ? COLOR_SUCCESS_BORDER : 'var(--border)'}`, borderRadius: 8, cursor: (reviewingId || !allChecked) ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 600, opacity: (reviewingId || !allChecked) ? 0.6 : 1 }}>
            {reviewingId === 'active' ? '…' : '✔ Approve'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function AgentReviewCard({ agent, categories, onReviewed }: { agent: any; categories: any[]; onReviewed: () => void }) {
  const [modalOpen, setModalOpen] = useState(false)
  const agentCats = categories.filter(c => agent.categoryIds?.includes(c.id))
  const statusLabel: Record<string, string> = { pending_review: 'Pending Review', active: 'Approved', rejected: 'Rejected' }

  return (
    <>
      <div onClick={() => setModalOpen(true)} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', transition: 'border-color 0.15s', borderLeft: `3px solid ${STATUS_COLOR[agent.status] ?? '#888'}` }} onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-primary)'} onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'}>
        <div style={{ width: 38, height: 38, borderRadius: 9, background: (agent.color ?? '#7c3aed') + '22', border: `1px solid ${agent.color ?? '#7c3aed'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{agent.icon ?? '🤖'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{agent.name}</span>
            <AgentStatusBadge status={agent.status} />
            {agent.resubmittedAt && <span style={{ padding: '0.12rem 0.45rem', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, background: COLOR_CYAN_ICON_BG, color: COLOR_CYAN }}>🔄 Resubmitted</span>}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.1rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>by <strong style={{ color: 'var(--text-secondary)' }}>{agent.developer?.orgName ?? 'Unknown'}</strong></span>
            {agent.developer?.user && <span>· {agent.developer.user.displayName}</span>}
            <span>· {timeAgoShort(agent.createdAt)}</span>
            {agentCats.slice(0, CATEGORY_BADGE_PREVIEW_MAX).map((cat: any) =><span key={cat.id} style={{ fontSize: '0.65rem', padding: '0.08rem 0.35rem', borderRadius: 20, background: `${cat.color ?? 'var(--accent-primary)'}18`, color: cat.color ?? 'var(--accent-primary)', border: `1px solid ${cat.color ?? 'var(--accent-primary)'}33` }}>{cat.icon} {cat.name}</span>)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          {agent.healthStatus && <span style={{ fontSize: '0.7rem', fontWeight: 600, color: agent.healthStatus === 'reachable' ? COLOR_SUCCESS : COLOR_ERROR }}>{agent.healthStatus === 'reachable' ? `● ${agent.healthLatencyMs}ms` : '● Unreachable'}</span>}
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>›</span>
        </div>
      </div>
      {modalOpen && <AgentReviewModal agent={agent} categories={categories} onReviewed={onReviewed} onClose={() => setModalOpen(false)} />}
    </>
  )
}

export function AdminExternalAgents() {
  const [statusFilter, setStatusFilter] = useState<string>('pending_review')
  const [search, setSearch] = useState('')
  const { data, isLoading, isFetching, refetch } = useGetAdminExternalAgentsQuery({ status: statusFilter })
  const { data: categoriesData } = useGetAdminCategoriesQuery()
  const [adminDeleteAgent] = useAdminDeleteAgentMutation()
  const categories = categoriesData?.categories ?? []
  const agents = data?.agents ?? []
  const STATUS_TABS = ['pending_review', 'active', 'rejected', 'inactive'] as const
  const statusLabel: Record<string, string> = { pending_review: 'Pending Review', active: 'Approved', rejected: 'Rejected', inactive: 'Withdrawn' }

  const filtered = search.trim()
    ? agents.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.developer?.orgName?.toLowerCase().includes(search.toLowerCase()) ||
        a.developer?.user?.displayName?.toLowerCase().includes(search.toLowerCase())
      )
    : agents

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Permanently remove "${name}"? This cannot be undone.`)) return
    await adminDeleteAgent(id).unwrap().catch(() => {})
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>🤖 External Agent Review</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search agents…"
            style={{
              padding: '0.35rem 0.65rem', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-elevated)', color: 'var(--text-primary)',
              fontSize: '0.8rem', outline: 'none', width: 200,
            }}
          />
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
        </div>
      </div>
      <Tabs tabs={STATUS_TABS.map(s => ({ key: s, label: statusLabel[s]! }))} active={statusFilter} onChange={(v) => { setStatusFilter(v); setSearch('') }} style={{ marginBottom: '1.25rem' }} />
      {isLoading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>}
      {!isLoading && filtered.length === 0 && (
        <EmptyState icon="🤖" title={search.trim() ? `No agents match "${search}"` : `No ${statusLabel[statusFilter]?.toLowerCase()} agents`} description="External agents submitted by developers will appear here" />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.map(agent => (
          statusFilter === 'inactive'
            ? (
              <div key={agent.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderLeft: `3px solid ${INACTIVE_BORDER}` }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: (agent.color ?? '#7c3aed') + '22', border: `1px solid ${agent.color ?? '#7c3aed'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{agent.icon ?? '🤖'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{agent.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>by <strong>{agent.developer?.orgName ?? 'Unknown'}</strong>{agent.developer?.user && <span> · {agent.developer.user.displayName}</span>}<span> · withdrawn {timeAgoShort(agent.updatedAt)}</span></div>
                </div>
                <Button size="sm" variant="danger" onClick={() => void handleDelete(agent.id, agent.name)}>Remove</Button>
              </div>
            )
            : <AgentReviewCard key={agent.id} agent={agent} categories={categories} onReviewed={() => void refetch()} />
        ))}
      </div>
    </div>
  )
}

